import type { EnvironmentId, ProjectId } from "@afar-research/contracts";

import type {
  SidebarProjectGroupMember,
  SidebarProjectSnapshot,
} from "../../sidebarProjectGrouping";

/**
 * Two axes, both carried in the URL so a settings link says exactly what it
 * was showing. `machine` narrows the Environment axis (absent = every
 * connected Environment); `project` narrows to one AFAR Project group (absent
 * = the Environment's own answers).
 *
 * There is deliberately no third axis for individual checkouts: each member
 * project of a group is already its own AFAR Project with its own id, so
 * selecting a group and a machine names exactly the projects to write to.
 *
 * Device-local preferences are not a scope. They render at every selection
 * because they never reach a server.
 */
export interface SettingsScopeSearch {
  project?: string;
  machine?: string;
}

export interface SettingsScopeTargets {
  readonly label: string;
  /** The (environment, project) pairs a write at this scope lands on. */
  readonly members: readonly SidebarProjectGroupMember[];
  readonly environmentIds: readonly EnvironmentId[];
}

export type ResolvedSettingsScope = SettingsScopeTargets &
  (
    | { kind: "all" }
    | { kind: "environment"; environmentId: EnvironmentId }
    | { kind: "project"; group: SidebarProjectSnapshot; environmentId: EnvironmentId | null }
    | {
        kind: "unavailable";
        reason: "project-missing" | "environment-missing" | "project-not-on-environment";
        message: string;
      }
  );

export interface SettingsScopeEnvironment {
  readonly environmentId: EnvironmentId;
  readonly label: string;
}

/**
 * Stale ids stay visible to the resolver on purpose. A selection whose target
 * has gone away must read as unavailable, never quietly widen to "every
 * Environment" — a write at the wrong scope is not recoverable by undoing a
 * navigation.
 */
export function validateSettingsScopeSearch(raw: Record<string, unknown>): SettingsScopeSearch {
  const text = (value: unknown) =>
    typeof value === "string" && value.trim().length > 0 ? value : undefined;
  const project = text(raw.project);
  const machine = text(raw.machine);
  return {
    ...(project === undefined ? {} : { project }),
    ...(machine === undefined ? {} : { machine }),
  };
}

export function resolveSettingsScope(
  search: SettingsScopeSearch,
  groups: readonly SidebarProjectSnapshot[],
  environments: readonly SettingsScopeEnvironment[],
): ResolvedSettingsScope {
  const unavailable = (
    reason: Extract<ResolvedSettingsScope, { kind: "unavailable" }>["reason"],
    message: string,
  ): ResolvedSettingsScope => ({
    kind: "unavailable",
    reason,
    label: "Unavailable selection",
    message,
    members: [],
    environmentIds: [],
  });

  const environment = environments.find((candidate) => candidate.environmentId === search.machine);
  if (search.machine !== undefined && environment === undefined) {
    return unavailable("environment-missing", "This environment is no longer available.");
  }

  if (search.project !== undefined) {
    const group = groups.find((candidate) => candidate.projectKey === search.project);
    if (group === undefined) {
      return unavailable("project-missing", "This project is no longer available.");
    }
    const members = group.memberProjects.filter(
      (member) => environment === undefined || member.environmentId === environment.environmentId,
    );
    if (members.length === 0) {
      return unavailable(
        "project-not-on-environment",
        "This project has no checkout on the selected environment.",
      );
    }
    return {
      kind: "project",
      group,
      environmentId: environment?.environmentId ?? null,
      label: `${group.displayName} / ${environment?.label ?? "All environments"}`,
      members,
      environmentIds: [...new Set(members.map((member) => member.environmentId))],
    };
  }

  if (environment !== undefined) {
    return {
      kind: "environment",
      environmentId: environment.environmentId,
      label: environment.label,
      members: [],
      environmentIds: [environment.environmentId],
    };
  }

  return {
    kind: "all",
    label: "All environments",
    members: [],
    environmentIds: environments.map((candidate) => candidate.environmentId),
  };
}

/** The AFAR Projects a project-scoped write lands on, by Environment. */
export function scopedProjectIdsByEnvironment(
  scope: ResolvedSettingsScope,
): ReadonlyMap<EnvironmentId, readonly ProjectId[]> {
  const byEnvironment = new Map<EnvironmentId, ProjectId[]>();
  for (const member of scope.members) {
    const existing = byEnvironment.get(member.environmentId);
    if (existing === undefined) byEnvironment.set(member.environmentId, [member.id]);
    else existing.push(member.id);
  }
  return byEnvironment;
}
